import React from 'react';
import { Box, Button, Chip, Container, Paper, Stack, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ArrowForward,
  AutoGraph,
  BarChart,
  Casino,
  CompareArrows,
  DocumentScanner,
  Hub,
  Insights,
  MenuBook,
  ReceiptLong,
  Timeline,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';

const img = (name: string) => `${process.env.PUBLIC_URL}/images/landing/${name}.jpg`;

// Only facts we can stand behind (see Research for the details behind each).
const FACTS = [
  { value: '20+', label: 'seasons of loop data' },
  { value: '35', label: 'tracks in the similarity model' },
  { value: '10k–50k', label: 'simulated races per run' },
  { value: '177', label: 'races replayed in our latest backtests' },
];

const FEATURES = [
  {
    icon: <Casino />,
    eyebrow: 'Race Simulator',
    title: 'Every finishing outcome, priced',
    body: 'Pick a track and the simulator replays the next race tens of thousands of times. You get win, top-3, top-5 and top-10 probabilities for the whole field, fair American odds, and a projected finish. Click any driver to unroll their form at a glance.',
    points: ['Percentages or American odds', 'Check any book price: edge, EV and a suggested stake', 'Head-to-head and group matchups with AI summaries'],
    image: 'odds',
    alt: 'Race Simulator results for Las Vegas in American odds, with a driver row unrolled to show their stats',
  },
  {
    icon: <AutoGraph />,
    eyebrow: 'Parlay builder',
    title: 'Same-race parlays, with the correlation included',
    body: 'Add legs straight from the results (top 10s, fades, head-to-heads, best-of-group) and price them together on the same simulated races, as a parlay or as “any leg hits”. Legs that move together are priced that way. Enter the book’s odds to see the edge, whether it beats our uncertainty, and a suggested stake.',
    points: ['Each leg alone and given the others', 'Parlay or “any leg hits” pricing', 'Edge vs. our uncertainty, with a Kelly stake'],
    image: 'parlay',
    alt: 'Parlay builder showing three legs, the joint probability, fair odds and a clear edge against the book with a suggested stake',
    narrow: true,
  },
  {
    icon: <CompareArrows />,
    eyebrow: 'Matchups',
    title: 'Head-to-heads and group bets, settled race by race',
    body: 'Check any two drivers, or a whole group, and the simulator plays them off against each other on the same simulated races: who finishes ahead, who finishes best in the group, and every pairwise head-to-head. An AI summary explains who it favors and why, with the drivers’ real head-to-head record in the races behind their ratings.',
    points: ['Head-to-head and best-of-group odds', 'AI summary with the real head-to-head record', 'Check a book’s matchup price in place'],
    image: 'matchup',
    alt: 'Group matchup for Kyle Larson, Denny Hamlin and Christopher Bell with an AI summary, best-of-group odds, a head-to-head grid and a price check',
  },
  {
    icon: <DocumentScanner />,
    eyebrow: 'Board scanner',
    title: 'Screenshot the board, get the best bets',
    body: 'Drop in a screenshot of your sportsbook’s race board and every line on it is read and priced against the simulation, with no typing in odds. Each price is read twice and checked, then you get the bets where the book is offering more than our odds say they’re worth, ranked by how much they’d grow a bankroll, weighing the edge against how often each bet hits.',
    points: ['Win, top-3, top-5 and top-10 boards', 'Which model has the edge: Base, Track Specialists or both', 'Stake as % of bankroll or units, and add picks to a parlay'],
    image: 'scanner',
    alt: 'Board scanner results for a Las Vegas board: three value picks with edge, EV, confidence and stake, and smaller edges within the model’s uncertainty',
  },
  {
    icon: <ReceiptLong />,
    eyebrow: 'Slip Builder',
    title: 'See how your whole slip plays out',
    body: 'Enter the bets you’re considering, with the book’s odds and your stakes, and every one is judged on the same simulated races, so bets that win or lose together are counted that way. You see which bets are worth it, your expected profit and chance of finishing ahead, the shape of what could happen, and a better way to stake the same money.',
    points: ['Value, too close or overpriced, against our margin of error', 'Profit curve with the likely range and break-even', 'Suggested stakes for more value or a steadier slip'],
    image: 'slip',
    alt: 'Slip Builder for Las Vegas: six bets with our chance, the book’s, the edge and a verdict, then expected profit, chance of profit and the profit curve against a suggested slip',
  },
  {
    icon: <Hub />,
    eyebrow: 'Track Specialists',
    title: 'Separate driver form from track skill',
    body: 'A second model alongside the Base model: each driver’s current form across every track of this kind, plus an adjustment for how much better or worse they run here than that form says. Flip between them and the table shows exactly who moves, from the same simulated races.',
    points: ['▲/▼ changes vs. the Base model', 'Form tracks and track-specific matches', 'Matchups, parlays and value checks follow the switch'],
    image: 'layer',
    alt: 'Simulator with Track Specialists switched on, showing up and down changes against the Base model',
  },
  {
    icon: <Timeline />,
    eyebrow: 'Track Similarity',
    title: 'Tracks compared by results, not banking',
    body: 'No need to pick comp tracks yourself. The model measures how alike every pair of tracks is from outcomes alone (whether the same drivers run well at both), then chooses the comparable tracks for each race and weights them automatically. A track-specific view removes general driver strength, so you can see which tracks really share a skill set and which only look alike because good drivers are good everywhere.',
    points: ['Comp tracks chosen and weighted for you', 'Predictive, character and track-specific views', 'Clear-link test with uncertainty'],
    image: 'similarity',
    alt: 'Track Similarity heatmap with the track-specific view and the details panel for Las Vegas',
  },
  {
    icon: <Insights />,
    eyebrow: 'Driver breakdowns',
    title: 'See exactly what a rating is built from',
    body: 'Open any driver to see every race behind their rating, how much each one counts, their track-specific adjustment, and a plain-English AI summary of why the simulation rates them the way it does. Chart their results race by race, and line them up against what the model predicted before each race to see who keeps beating expectations.',
    points: ['Race-by-race weights', '"Why this rating" AI summaries', 'Actual finishes vs. the model’s predictions'],
    image: 'modal',
    alt: 'Driver breakdown for Kyle Larson: his stats, AI summary and a race-by-race chart of his finishes against the model’s predictions',
  },
  {
    icon: <BarChart />,
    eyebrow: 'Race charts',
    title: 'Every race behind the numbers, charted',
    body: 'Chart the races behind any driver or matchup: finishes, running position, laps led, passing and more over time; how often each driver wins, runs top 5 or wrecks out; who beats whom in the races they’ve shared, track by track; and which drivers ran better than their results. Slide to zoom in on recent races.',
    points: ['Trends for any stat, all races or this track only', 'Head to head vs. one driver or the whole group', 'Finish spread and speed vs. result'],
    image: 'charts',
    alt: 'Head-to-head chart for Kyle Larson against Denny Hamlin and Christopher Bell over the last 30 races, split by which driver finished best, overall and by track',
  },
  {
    icon: <MenuBook />,
    eyebrow: 'Research',
    title: 'The methods, and the numbers behind them',
    body: 'Every model change is backtested by replaying past races with only the data available beforehand. The Research section publishes how the models work and the actual results, including the experiments that didn’t make the cut.',
    points: ['Methodology, guides and case studies', 'Calibration and backtest results', 'Updated as the model evolves'],
    image: 'research',
    alt: 'Research section listing articles on the simulator, track similarity, calibration and backtesting',
  },
];

const STEPS = [
  {
    n: '1',
    title: 'Score every result',
    body: 'Each finish and average running position becomes a percentile score, so a 10th in a 36-car field and a 10th in a 40-car field compare fairly.',
  },
  {
    n: '2',
    title: 'Rate drivers where it matters',
    body: 'Ratings weight recent races and the tracks whose results really resemble this one, then shrink thin evidence toward a sensible default.',
  },
  {
    n: '3',
    title: 'Simulate the race',
    body: 'Each simulated race draws every driver’s performance and DNF chance. The share of races where an outcome happens is its probability.',
  },
];

/** A screenshot in a simple browser-style frame. */
const Shot: React.FC<{ src: string; alt: string; eager?: boolean; narrow?: boolean }> = ({ src, alt, eager, narrow }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        // Portrait shots (the parlay builder) are shown narrower so they don't tower over the text.
        ...(narrow && { maxWidth: 400, mx: 'auto' }),
        borderRadius: 3,
        overflow: 'hidden',
        border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
        boxShadow:
          theme.palette.mode === 'dark' ? '0 24px 60px rgba(0,0,0,0.55)' : '0 24px 60px rgba(15, 40, 20, 0.18)',
        bgcolor: '#121212',
      }}
    >
      <Stack direction="row" spacing={0.75} sx={{ px: 1.5, py: 1, bgcolor: '#1f1f1f' }}>
        {['#ff5f57', '#febc2e', '#28c840'].map((c) => (
          <Box key={c} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c, opacity: 0.85 }} />
        ))}
      </Stack>
      <Box
        component="img"
        src={src}
        alt={alt}
        loading={eager ? 'eager' : 'lazy'}
        sx={{
          display: 'block',
          width: '100%',
          height: 'auto',
        }}
      />
    </Paper>
  );
};

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const green = theme.palette.primary.main;

  const primaryCta = isAuthenticated
    ? { label: 'Open the Simulator', onClick: () => navigate('/simulator') }
    : { label: 'Log in to start', onClick: () => loginWithRedirect() };

  return (
    <Box sx={{ minHeight: '100vh' }}>
      {/* Hero */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          pt: { xs: 7, md: 10 },
          pb: { xs: 6, md: 8 },
          background:
            theme.palette.mode === 'dark'
              ? `radial-gradient(1000px 500px at 85% -10%, ${alpha(green, 0.18)}, transparent 60%), #121212`
              : `radial-gradient(1000px 500px at 85% -10%, ${alpha(green, 0.16)}, transparent 60%), #f5f5f5`,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 820, mx: 'auto', textAlign: 'center' }}>
            <Chip label="Stock car racing analytics" color="primary" variant="outlined" sx={{ mb: 3, fontWeight: 600 }} />
            <Typography
              component="h1"
              sx={{ fontWeight: 800, fontSize: { xs: '2.4rem', md: '3.6rem' }, lineHeight: 1.1, letterSpacing: '-0.02em', mb: 2.5 }}
            >
              Simulate the race{' '}
              <Box component="span" sx={{ color: green }}>
                before it runs.
              </Box>
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, lineHeight: 1.6, mb: 4 }}>
              A stock car race simulator built on 20+ seasons of loop data. It rates drivers on the tracks that truly
              resemble each race, prices every outcome and parlay, and shows you exactly why.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                size="large"
                onClick={primaryCta.onClick}
                endIcon={<ArrowForward />}
                sx={{ py: 1.5, px: 4, fontWeight: 700, borderRadius: 2 }}
              >
                {primaryCta.label}
              </Button>
              <Button
                variant="outlined"
                size="large"
                href="#how-it-works"
                sx={{ py: 1.5, px: 4, fontWeight: 700, borderRadius: 2 }}
              >
                How it works
              </Button>
            </Stack>
          </Box>
          <Box sx={{ mt: { xs: 6, md: 8 }, maxWidth: 1100, mx: 'auto' }}>
            <Shot src={img('simulator')} alt={FEATURES[0].alt} eager />
          </Box>
        </Container>
      </Box>

      {/* Facts */}
      <Box sx={{ borderTop: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: { xs: 3, md: 2 },
              py: { xs: 4, md: 5 },
            }}
          >
            {FACTS.map((f) => (
              <Box key={f.label} sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.2rem' }, color: green, lineHeight: 1.1 }}>
                  {f.value}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {f.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Features */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Stack spacing={{ xs: 10, md: 14 }}>
          {FEATURES.map((f, i) => (
            <Box
              key={f.eyebrow}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: i % 2 === 0 ? '5fr 7fr' : '7fr 5fr' },
                gap: { xs: 4, md: 7 },
                alignItems: 'center',
              }}
            >
              <Box sx={{ order: { xs: 0, md: i % 2 === 0 ? 0 : 1 } }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ color: green, mb: 1.5 }}>
                  {f.icon}
                  <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
                    {f.eyebrow}
                  </Typography>
                </Stack>
                <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.7rem', md: '2.1rem' }, lineHeight: 1.2, mb: 2 }}>
                  {f.title}
                </Typography>
                <Typography color="text.secondary" sx={{ lineHeight: 1.75, mb: 2.5 }}>
                  {f.body}
                </Typography>
                <Stack spacing={1}>
                  {f.points.map((p) => (
                    <Stack key={p} direction="row" spacing={1.25} alignItems="center">
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: green, flexShrink: 0 }} />
                      <Typography variant="body2">{p}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
              <Box sx={{ order: { xs: 1, md: i % 2 === 0 ? 1 : 0 } }}>
                <Shot src={img(f.image)} alt={f.alt} narrow={'narrow' in f && !!f.narrow} />
              </Box>
            </Box>
          ))}
        </Stack>
      </Container>

      {/* How it works */}
      <Box id="how-it-works" sx={{ bgcolor: theme.palette.mode === 'dark' ? '#181818' : '#ececec', py: { xs: 8, md: 11 }, scrollMarginTop: 64 }}>
        <Container maxWidth="lg">
          <Typography component="h2" align="center" sx={{ fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.3rem' }, mb: 1.5 }}>
            How it works
          </Typography>
          <Typography align="center" color="text.secondary" sx={{ maxWidth: 680, mx: 'auto', mb: 6 }}>
            Three steps, each tested by replaying past races with only the data that existed before them.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
            {STEPS.map((s) => (
              <Paper key={s.n} variant="outlined" sx={{ p: 3.5, borderRadius: 3, bgcolor: 'background.paper' }}>
                <Typography sx={{ fontWeight: 800, fontSize: '2rem', color: green, lineHeight: 1, mb: 1.5 }}>{s.n}</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  {s.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  {s.body}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Closing CTA */}
      <Container maxWidth="md" sx={{ py: { xs: 8, md: 11 }, textAlign: 'center' }}>
        <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.3rem' }, mb: 2 }}>
          Run this week&apos;s race.
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 4 }}>
          Pick a track, simulate it, and build your card from the numbers.
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={primaryCta.onClick}
          endIcon={<ArrowForward />}
          sx={{ py: 1.5, px: 5, fontWeight: 700, borderRadius: 2 }}
        >
          {primaryCta.label}
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 5, maxWidth: 620, mx: 'auto' }}>
          Probabilities are model estimates for analysis and entertainment, not guarantees or betting advice. Please bet
          responsibly.
        </Typography>
      </Container>
    </Box>
  );
};

export default LandingPage;
